import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'ghost' | 'outline' | 'danger'
  size?: 'sm' | 'md' | 'icon'
}

/** Copied from ../../agent-house/src/components/ui/Button.tsx for visual parity. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          'disabled:opacity-50 disabled:pointer-events-none',
          {
            'bg-brand-500 text-white hover:bg-brand-400 active:bg-brand-600 disabled:!opacity-70':
              variant === 'primary',
            'text-slate-300 hover:bg-slate-700 hover:text-slate-100': variant === 'ghost',
            'border border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-slate-100':
              variant === 'outline',
            // `text-red-50`, not `text-white`: red is a themed ramp, so in the
            // light theme this fill is a pale red and white text on it is
            // invisible. Shade 50 is the ramp's contrasting end in either theme.
            'bg-red-800 text-red-50 hover:bg-red-700': variant === 'danger',
            'h-8 px-3 text-sm': size === 'sm',
            'h-10 px-4 text-sm': size === 'md',
            'h-9 w-9 p-0': size === 'icon',
          },
          className,
        )}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'
